"use client";

import {
  Button,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React from "react";

import { useRouter } from "@/i18n/routing";
import {
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
} from "@/lib/firebase-auth";
import Link from "next/link";

export default function LoginForm() {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const [isResetting, setIsResetting] = React.useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";

  const toggleVisibility = () => setIsVisible(!isVisible);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const { user } = await signInWithGoogle();

      if (user) {
        router.push(redirectTo);
      } else {
        setIsLoading(false);
      }
    } catch {
      addToast({
        title: t("auth.login.googleSignInFailed"),
        color: "danger",
        severity: "danger",
      });
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = e.currentTarget.email.value;
    const password = e.currentTarget.password.value;

    const { error } = await signInWithEmail(email, password);

    if (error) {
      addToast({
        title: t("auth.login.signInFailed"),
        severity: "danger",
      });
    } else {
      router.push(redirectTo);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      addToast({
        title: t("auth.login.resetEmailRequired"),
        severity: "warning",
      });

      return;
    }

    setIsResetting(true);

    const { error } = await sendPasswordReset(resetEmail);

    if (error) {
      addToast({
        title: t("auth.login.resetFailed"),
        severity: "danger",
      });
    } else {
      addToast({
        title: t("auth.login.resetSuccess"),
        severity: "success",
      });
      onClose();
      setResetEmail("");
    }
    setIsResetting(false);
  };

  return (
    <div className="mx-auto w-full max-w-sm">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <Input
          isRequired
          label={t("auth.login.email")}
          name="email"
          placeholder={t("auth.login.emailPlaceholder")}
          type="email"
          variant="bordered"
          autoComplete="email"
        />
        <Input
          isRequired
          endContent={
            <button type="button" onClick={toggleVisibility}>
              {isVisible ? (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-closed-linear"
                />
              ) : (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-bold"
                />
              )}
            </button>
          }
          label={t("auth.login.password")}
          name="password"
          placeholder={t("auth.login.passwordPlaceholder")}
          type={isVisible ? "text" : "password"}
          variant="bordered"
          autoComplete="current-password"
        />

        <div className="flex items-center justify-end">
          <button
            className="text-sm text-primary hover:underline"
            type="button"
            onClick={onOpen}
          >
            {t("auth.login.forgotPassword")}
          </button>
        </div>

        <Button color="primary" size="lg" type="submit">
          {t("auth.login.signIn")}
        </Button>
      </form>

      <div className="flex items-center gap-4 py-4">
        <Divider className="flex-1" />
        <p className="shrink-0 text-tiny text-default-500">
          {t("auth.login.or")}
        </p>
        <Divider className="flex-1" />
      </div>

      <div className="flex flex-col gap-2">
        <Button
          isLoading={isLoading}
          size="lg"
          startContent={<Icon icon="flat-color-icons:google" width={24} />}
          variant="bordered"
          onPress={handleGoogleSignIn}
        >
          {t("auth.login.continueWithGoogle")}
        </Button>

        <Link
          className="text-center text-primary hover:underline"
          href="/register"
        >
          {t("auth.login.needAccount")}&nbsp;{t("auth.login.signUp")}
        </Link>
      </div>

      <p className="mt-6 text-center text-small"></p>

      <Modal isOpen={isOpen} placement="center" onClose={onClose}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {t("auth.login.resetPasswordTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="text-small text-default-500">
              {t("auth.login.resetPasswordDescription")}
            </p>
            <Input
              label={t("auth.login.email")}
              placeholder={t("auth.login.emailPlaceholder")}
              type="email"
              value={resetEmail}
              variant="bordered"
              onChange={(e) => setResetEmail(e.target.value)}
              autoComplete="email"
            />
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              {t("auth.login.cancel")}
            </Button>
            <Button
              color="primary"
              isLoading={isResetting}
              onPress={handlePasswordReset}
            >
              {t("auth.login.sendResetLink")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
